import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { getLeaderboard, getCurrentMember } from '../services/api';
import Larry from './Larry';
import usePageTitle from '../hooks/usePageTitle';

const PERIODS = [
  { id: 'all', label: 'All time' },
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'Past 3 months' },
];

export default function Leaderboard() {
  usePageTitle('Leaderboard');
  const [entries, setEntries] = useState([]);
  const [period, setPeriod] = useState('all');
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getCurrentMember()
      .then((res) => { if (res?.member) setMember(res.member); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLeaderboard(period);
      setEntries(res?.leaderboard || []);
    } catch (err) {
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <Larry mood="sad" size={72} message="Couldn't load the leaderboard." />
        <p className="text-sm" style={{ color: 'var(--text-faint)' }}>{error}</p>
        <button onClick={loadLeaderboard} className="btn-bouncy text-sm px-5 py-2">
          Try Again
        </button>
      </div>
    );
  }

  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Larry mood={entries.length > 0 ? 'excited' : 'thinking'} size={48} />
          <div>
            <h1 className="font-display text-2xl font-semibold" style={{ color: 'var(--accent)' }}>
              Leaderboard
            </h1>
            <p className="font-mono text-xs uppercase tracking-wide mt-0.5" style={{ color: 'var(--text-faint)' }}>
              Points earned by showing up and speaking
            </p>
          </div>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="flex gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={clsx('pill-tab', period === p.id && 'active')}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12 gap-3">
          <Larry mood="thinking" size={56} message="Tallying the scores…" />
        </div>
      ) : entries.length === 0 ? (
        <div className="card-paper p-8 flex flex-col items-center text-center gap-3">
          <Larry mood="happy" size={64} message="No activity in this period — try 'All time'" />
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
            Show up. Give speeches. Climb.
          </p>
        </div>
      ) : (
        <div className="space-y-6 stagger-in">
          {/* Podium — Top 3 */}
          {top3.length > 0 && (
            <div
              className="rounded-2xl p-4 sm:p-6"
              style={{ background: 'linear-gradient(180deg, var(--accent-dim) 0%, var(--bg-canvas) 100%)', border: '1px solid var(--border-subtle)' }}
            >
              <div className="grid grid-cols-3 gap-2 sm:gap-3 items-end">
                {/* 2nd place (left) */}
                {top3.length > 1 ? (
                  <PodiumCard entry={top3[1]} rank={2} currentMemberId={member?.member_id} />
                ) : <div />}

                {/* 1st place (center, tallest) */}
                <PodiumCard entry={top3[0]} rank={1} currentMemberId={member?.member_id} />

                {/* 3rd place (right) */}
                {top3.length > 2 ? (
                  <PodiumCard entry={top3[2]} rank={3} currentMemberId={member?.member_id} />
                ) : <div />}
              </div>
            </div>
          )}

          {/* Rest of leaderboard */}
          {rest.length > 0 && (
            <div className="card-paper overflow-hidden">
              {rest.map((entry, index) => {
                const rank = index + 4;
                const isMe = entry.member_id === member?.member_id;

                return (
                  <div
                    key={entry.member_id}
                    className={clsx(
                      'flex items-center px-4 py-3 transition-colors',
                      'border-b last:border-b-0'
                    )}
                    style={isMe
                      ? { borderColor: 'var(--border-accent)', background: 'var(--accent-dim)' }
                      : { borderColor: 'var(--border-subtle)' }
                    }
                    onMouseEnter={e => { if (!isMe) e.currentTarget.style.background = 'var(--accent-dim)'; }}
                    onMouseLeave={e => { if (!isMe) e.currentTarget.style.background = ''; }}
                  >
                    {/* Rank */}
                    <span className="w-8 text-center text-sm font-extrabold font-mono" style={{ color: 'var(--text-faint)' }}>
                      {rank}
                    </span>

                    {/* Avatar */}
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold ml-2"
                      style={isMe
                        ? { background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--border-accent)' }
                        : { background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }
                      }
                    >
                      {entry.member_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    {/* Name */}
                    <div className="ml-3 flex-1 min-w-0">
                      <span
                        className="text-sm font-bold truncate block"
                        style={{ color: isMe ? 'var(--accent)' : 'var(--text-primary)' }}
                      >
                        {entry.member_name || 'Member'}
                        {isMe && <span className="ml-1 text-xs" style={{ color: 'var(--accent)' }}>(You)</span>}
                      </span>
                    </div>

                    {/* Breakdown */}
                    <div className="hidden sm:flex items-center gap-4 text-xs font-bold" style={{ color: 'var(--text-faint)' }}>
                      <span>{entry.attendance_count ?? 0} attended</span>
                      <span>{entry.speech_count ?? 0} {(entry.speech_count ?? 0) === 1 ? 'speech' : 'speeches'}</span>
                    </div>

                    {/* Total */}
                    <span
                      className="ml-4 text-lg font-extrabold tabular-nums font-mono"
                      style={{ color: isMe ? 'var(--accent)' : 'var(--text-secondary)' }}
                    >
                      {entry.total ?? 0} pts
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PODIUM_CONFIG = {
  1: {
    medalEmoji: '👑',
    avatarSize: 'w-12 h-12 sm:w-16 sm:h-16',
    textSize: 'text-sm sm:text-base',
    padding: 'pt-4 pb-6 sm:pt-6 sm:pb-8',
    glowStyle: { boxShadow: '0 0 40px rgba(255,215,0,0.5), 0 0 80px rgba(255,215,0,0.2)' },
    pointsColor: 'var(--color-warning)',
    avatarBg: 'rgba(255,215,0,0.15)',
    avatarColor: '#FFD60A',
    avatarBorder: 'rgba(255,215,0,0.5)',
    nameColor: 'var(--color-warning)',
  },
  2: {
    medalEmoji: '🥈',
    avatarSize: 'w-10 h-10 sm:w-12 sm:h-12',
    textSize: 'text-xs sm:text-sm',
    padding: 'pt-3 pb-5 sm:pt-4 sm:pb-6',
    glowStyle: { boxShadow: '0 0 20px rgba(192,192,192,0.4)' },
    pointsColor: 'var(--text-secondary)',
    avatarBg: 'rgba(192,192,192,0.1)',
    avatarColor: '#C0C0C0',
    avatarBorder: 'rgba(192,192,192,0.3)',
    nameColor: 'var(--text-primary)',
  },
  3: {
    medalEmoji: '🥉',
    avatarSize: 'w-8 h-8 sm:w-10 sm:h-10',
    textSize: 'text-xs sm:text-sm',
    padding: 'pt-3 pb-4 sm:pt-3 sm:pb-5',
    glowStyle: { boxShadow: '0 0 20px rgba(205,127,50,0.4)' },
    pointsColor: 'var(--text-muted)',
    avatarBg: 'rgba(205,127,50,0.1)',
    avatarColor: '#CD7F32',
    avatarBorder: 'rgba(205,127,50,0.3)',
    nameColor: 'var(--text-primary)',
  },
};

function PodiumCard({ entry, rank, currentMemberId }) {
  const cfg = PODIUM_CONFIG[rank];
  const isMe = entry.member_id === currentMemberId;

  return (
    <div
      className={clsx('flex flex-col items-center text-center rounded-2xl', cfg.padding)}
      style={{
        background: 'var(--bg-input)',
        border: '1px solid var(--border-subtle)',
        ...cfg.glowStyle,
      }}
    >
      {/* Crown / Medal */}
      <span className={clsx('text-2xl mb-2', rank === 1 && 'animate-float')}>
        {cfg.medalEmoji}
      </span>

      {/* Avatar */}
      <div
        className={clsx(cfg.avatarSize, 'rounded-full flex items-center justify-center font-extrabold mb-2 ring-2')}
        style={{
          background: isMe ? 'var(--accent-dim)' : cfg.avatarBg,
          color: isMe ? 'var(--accent)' : cfg.avatarColor,
          border: `2px solid ${isMe ? 'var(--border-accent)' : cfg.avatarBorder}`,
          fontSize: rank === 1 ? '1.25rem' : '1rem',
        }}
      >
        {entry.member_name?.charAt(0)?.toUpperCase() || '?'}
      </div>

      {/* Name */}
      <p className={clsx(cfg.textSize, 'font-extrabold truncate max-w-full px-2')}>
        <span style={{ color: isMe ? 'var(--accent)' : cfg.nameColor }}>
          {entry.member_name}
        </span>
        {isMe && <span className="block text-xs" style={{ color: 'var(--accent)' }}>(You!)</span>}
      </p>

      {/* Points */}
      <p className="text-lg sm:text-xl font-black mt-1 sm:mt-2 tabular-nums font-mono" style={{ color: cfg.pointsColor }}>
        {entry.total}
      </p>
      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>pts</p>

      {/* Breakdown */}
      <div className="flex items-center gap-3 mt-1 text-xs font-bold" style={{ color: 'var(--text-faint)' }}>
        <span>{entry.attendance_count ?? 0} attended</span>
        <span>{entry.speech_count ?? 0} {(entry.speech_count ?? 0) === 1 ? 'speech' : 'speeches'}</span>
      </div>
    </div>
  );
}
